import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, Button, FormHelperText, IconButton, TextField } from "@mui/material";
import { FC, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { Formik } from "formik";
import * as Yup from "yup";
import useMounted from "hooks/useMounted";
import useLoginGuard from "hooks/useLoginGuard";
import useAuth from "hooks/useAuth";
import PasswordPolicyHint, { combinationPatt } from "./PasswordPolicyHint";

const NewPasswordRequiredFormFormik: FC = props => {
    const mounted = useMounted();
    const { settings, saveSettings } = useLoginGuard();
    const { forceChangePassword, login } = useAuth();

    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showPassword2, setShowPassword2] = useState<boolean>(false);

    return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Formik
                initialValues={{
                    password: "",
                    confirmPassword: "",
                    submit: null
                }}
                validationSchema={Yup.object({
                    password: Yup.string()
                        .max(255)
                        .required("Password is required")
                        .matches(
                            combinationPatt,
                            "Password must contain at least 8 characters, one uppercase, one number and one special case character"
                        ),
                    confirmPassword: Yup.string().oneOf(
                        [Yup.ref("password"), null],
                        "Passwords must match"
                    )
                })}
                onSubmit={async (
                    values,
                    { setErrors, setStatus, setSubmitting }
                ): Promise<void> => {
                    try {
                        await forceChangePassword(values.password);
                        const res = await login(settings.currEmail, values.password);
                        // MFA_SETUP  need to scan the qrcode
                        if (res === "MFA_SETUP") {
                            saveSettings({
                                ...settings,
                                currStep: "MFA_SETUP"
                            });
                            return;
                        }
                        saveSettings({
                            ...settings,
                            currStep: "LOGIN"
                        });
                        if (mounted.current) {
                            setStatus({ success: true });
                            setSubmitting(false);
                        }
                    } catch (err: any) {
                        console.error(err);
                        if (mounted.current) {
                            setStatus({ success: false });
                            setErrors({ submit: err.message });
                            setSubmitting(false);
                        }
                    }
                }}>
                {({
                    errors,
                    handleChange,
                    handleSubmit,
                    isSubmitting,
                    touched,
                    values
                }): JSX.Element => (
                    <form noValidate onSubmit={handleSubmit} {...props} style={{ width: "100%" }}>
                        <PasswordPolicyHint password={values.password} />
                        <input
                            type="text"
                            name="_prevent_auto_complete_name"
                            autoComplete="off"
                            readOnly
                            style={{ display: "none" }}
                        />
                        <input
                            type="password"
                            name="_prevent_auto_complete_pass"
                            autoComplete="new-password"
                            readOnly
                            style={{ display: "none" }}
                        />
                        <TextField
                            autoFocus
                            error={Boolean(touched.password && errors.password)}
                            fullWidth
                            helperText={touched.password && errors.password}
                            label="Password"
                            margin="normal"
                            name="password"
                            onChange={handleChange}
                            type={showPassword ? "text" : "password"}
                            value={values.password}
                            variant="standard"
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        tabIndex={-1}
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? (
                                            <VisibilityOff fontSize="small" />
                                        ) : (
                                            <Visibility fontSize="small" />
                                        )}
                                    </IconButton>
                                )
                            }}
                        />
                        <TextField
                            error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                            fullWidth
                            helperText={touched.confirmPassword && errors.confirmPassword}
                            label="Confirm password"
                            margin="normal"
                            name="confirmPassword"
                            onChange={handleChange}
                            type={showPassword2 ? "text" : "password"}
                            value={values.confirmPassword}
                            variant="standard"
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        tabIndex={-1}
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword2(!showPassword2)}>
                                        {showPassword2 ? (
                                            <VisibilityOff fontSize="small" />
                                        ) : (
                                            <Visibility fontSize="small" />
                                        )}
                                    </IconButton>
                                )
                            }}
                        />
                        {errors.submit && (
                            <Box>
                                <FormHelperText error>{errors.submit}</FormHelperText>
                            </Box>
                        )}
                        <Box
                            sx={{
                                mt: 5,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center"
                            }}>
                            <Button
                                color="primary"
                                onClick={() => {
                                    saveSettings({
                                        ...settings,
                                        currStep: "LOGIN"
                                    });
                                }}>
                                CANCEL
                            </Button>
                            <LoadingButton
                                color="primary"
                                sx={{ height: 39, fontSize: 14 }}
                                loading={isSubmitting}
                                size="large"
                                type="submit"
                                variant="contained">
                                UPDATE PASSWORD
                            </LoadingButton>
                        </Box>
                    </form>
                )}
            </Formik>
        </Box>
    );
};

export default NewPasswordRequiredFormFormik;
